import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import {
  Shield, CheckCircle2, AlertTriangle, XCircle, MinusCircle,
  Clock, Bell, ExternalLink, ChevronDown, ChevronUp, RefreshCw,
  Building2, Receipt, Users, ShieldAlert, Briefcase, Sparkles
} from 'lucide-react';

// ── Types ──
interface ComplianceItem {
  id: string;
  category: string;
  industry: string;
  title_zh: string;
  title_en?: string;
  description_zh?: string;
  is_required: boolean;
  has_deadline: boolean;
  deadline_field?: string;
  action_url?: string;
  action_label_zh?: string;
  sort_order: number;
  member_status: 'compliant' | 'pending' | 'overdue' | 'not_applicable';
  notes?: string;
  completed_at?: string;
}

interface Reminder {
  id: string;
  date_type: string;
  date_value: string;
  reminder_days: string;
}

interface ComplianceData {
  stats: { compliant: number; pending: number; overdue: number; not_applicable: number; total: number };
  categories: Record<string, ComplianceItem[]>;
  checklist: ComplianceItem[];
  company_info?: {
    br_number?: string;
    br_expiry_date?: string;
    ci_number?: string;
    industry?: string;
    employee_count?: string;
    fiscal_year_end?: string;
    secretary_name?: string;
    auditor_name?: string;
  };
  upcoming_reminders: Reminder[];
}

// ── Localized metadata ──
const CATEGORY_NAMES: Record<string, { icon: React.ElementType; color: string; en: string; zhHant: string; zhHans: string }> = {
  company: { icon: Building2, color: 'text-blue-600', en: 'Company basics', zhHant: '公司基本', zhHans: '公司基本' },
  tax: { icon: Receipt, color: 'text-orange-900 dark:text-orange-200', en: 'Tax', zhHant: '稅務', zhHans: '税务' },
  employment: { icon: Users, color: 'text-indigo-600', en: 'Employment', zhHant: '僱傭相關', zhHans: '雇佣相关' },
  privacy: { icon: ShieldAlert, color: 'text-blue-600', en: 'Data privacy', zhHant: '資料私隱', zhHans: '资料私隐' },
  industry: { icon: Briefcase, color: 'text-indigo-600', en: 'Industry specific', zhHant: '行業特定', zhHans: '行业特定' },
};

const DATE_TYPE_NAMES: Record<string, { en: string; zhHant: string; zhHans: string }> = {
  br_expiry: { en: 'Business Registration expiry', zhHant: '商業登記證到期', zhHans: '商业登记证到期' },
  annual_return: { en: 'Annual Return', zhHant: '周年申報表', zhHans: '周年申报表' },
  tax_filing_deadline: { en: 'Profits Tax filing deadline', zhHant: '利得稅報稅死線', zhHans: '利得税报税死线' },
  audit_deadline: { en: 'Audit deadline', zhHant: '審計死線', zhHans: '审计死线' },
  mpf_due: { en: 'MPF contribution due', zhHant: 'MPF 供款', zhHans: 'MPF 供款' },
  insurance_expiry: { en: 'Employees’ Compensation insurance expiry', zhHant: '勞工保險到期', zhHans: '劳工保险到期' },
  custom: { en: 'Custom deadline', zhHant: '自訂', zhHans: '自定义' },
};

function categoryLabel(key: string): string {
  const category = CATEGORY_NAMES[key];
  return category ? tr(category.en, category.zhHant, category.zhHans) : key;
}

function dateTypeLabel(key: string): string {
  const type = DATE_TYPE_NAMES[key];
  return type ? tr(type.en, type.zhHant, type.zhHans) : key;
}

function complianceStatus(status: string): { key: string; icon: React.ElementType } {
  switch (status) {
    case 'compliant': return { key: 'compliant', icon: CheckCircle2 };
    case 'overdue': return { key: 'overdue', icon: XCircle };
    case 'not_applicable': return { key: 'not_applicable', icon: MinusCircle };
    default: return { key: 'pending', icon: AlertTriangle };
  }
}

function complianceStatusLabel(status: string): string {
  switch (status) {
    case 'compliant': return tr('Compliant', '合規', '合规');
    case 'overdue': return tr('Overdue', '逾期', '逾期');
    case 'not_applicable': return tr('Not applicable', '不適用', '不适用');
    default: return tr('Pending', '待處理', '待处理');
  }
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'compliant': return 'bg-green-100 text-green-900';
    case 'overdue': return 'bg-red-100 text-red-900';
    case 'not_applicable': return 'bg-gray-100 text-gray-900';
    default: return 'bg-amber-100 text-amber-950';
  }
}

function daysLeft(dateStr: string): number {
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

// ── Main Page ──
export default function Compliance() {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const [expandedCategory, setExpandedCategory] = useState<string | null>('company');
  const [searchParams] = useSearchParams();
  const highlightTitle = searchParams.get('title');

  const { data, isLoading, isError } = useQuery<ComplianceData>({
    queryKey: ['compliance'],
    queryFn: () => api('/compliance'),
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api(`/compliance/${id}`, { method: 'PUT', body: { status } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['compliance'] }),
  });

  // Deep-link a dashboard deadline to its checklist row. Match by title because
  // the dashboard selects compliance templates, not compliance_dates rows.
  useEffect(() => {
    if (!highlightTitle || !data?.checklist) return;
    const normalize = (value: unknown) => String(value || '').trim().toLowerCase();
    const target = normalize(highlightTitle);
    const item = data.checklist.find((entry: ComplianceItem) => (
      normalize(entry.title_en) === target || normalize(entry.title_zh) === target
    ));
    if (!item) return;

    setExpandedCategory(item.category || 'other');
    let attempts = 0;
    const highlightRow = () => {
      attempts += 1;
      const row = document.getElementById(`compliance-item-${item.id}`);
      if (!row) {
        if (attempts < 12) setTimeout(highlightRow, 100);
        return;
      }
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      row.classList.add('ring-2', 'ring-blue-400');
      setTimeout(() => row.classList.remove('ring-2', 'ring-blue-400'), 3000);
    };
    const timer = setTimeout(highlightRow, 150);
    return () => clearTimeout(timer);
  }, [highlightTitle, data]);

  const localizedItemTitle = (item: ComplianceItem) => tr(
    item.title_en || item.title_zh,
    item.title_zh,
    item.title_zh,
  );

  const localizedItemDescription = (item: ComplianceItem) => tr(
    item.title_en || item.description_zh || '',
    item.description_zh || '',
    item.description_zh || '',
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-16">
        <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">
          {tr('Unable to load compliance data. Please try again later.', '無法載入合規資料，請稍後再試', '无法载入合规数据，请稍后再试')}
        </p>
      </div>
    );
  }

  const { stats, categories, upcoming_reminders, company_info } = data!;
  const isEnglish = i18n.language === 'en';

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            {tr('Company Documents', '公司文件', '公司文件')}
          </h2>
          <p className="text-muted-foreground mt-1">
            {company_info?.industry
              ? tr(`Industry: ${company_info.industry}`, `行業：${company_info.industry}`, `行业：${company_info.industry}`)
              : tr('Company records, filing status, and key deadlines.', '公司文件、申報狀態及重要期限。', '公司文件、申报状态及重要期限。')}
          </p>
        </div>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['compliance'] })}
          className="btn-outline flex items-center gap-2 text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          {tr('Refresh', '更新', '更新')}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard label={tr('Compliant', '合規', '合规')} value={stats.compliant} color="text-green-900" bg="bg-green-50" icon={CheckCircle2} />
        <StatCard label={tr('Pending', '待處理', '待处理')} value={stats.pending} color="text-amber-950" bg="bg-amber-50" icon={AlertTriangle} />
        <StatCard label={tr('Overdue', '逾期', '逾期')} value={stats.overdue} color="text-red-900" bg="bg-red-50" icon={XCircle} />
        <StatCard label={tr('Not applicable', '不適用', '不适用')} value={stats.not_applicable} color="text-gray-900" bg="bg-gray-50" icon={MinusCircle} />
      </div>

      {/* Upcoming Deadlines */}
      {upcoming_reminders.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <h3 className="mb-3 flex items-center gap-2 font-semibold text-red-900">
            <Bell className="h-4 w-4" />
            {tr('Upcoming reminders', '即將到期提醒', '即将到期提醒')}
          </h3>
          <div className="space-y-2">
            {upcoming_reminders.map((reminder) => {
              const remainingDays = daysLeft(reminder.date_value);
              const daysText = remainingDays === 1
                ? tr('1 day', '1 天', '1 天')
                : tr(`${remainingDays} days`, `${remainingDays} 天`, `${remainingDays} 天`);
              return (
                <div key={reminder.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-red-900" />
                    <span className="font-medium text-foreground">{dateTypeLabel(reminder.date_type)}</span>
                    <span className="text-muted-foreground">{reminder.date_value}</span>
                  </div>
                  <span className={`font-bold ${remainingDays <= 30 ? 'text-red-900' : remainingDays <= 60 ? 'text-amber-950' : 'text-muted-foreground'}`}>
                    {daysText}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Checklist by Category */}
      <div className="space-y-3">
        {Object.entries(categories).map(([categoryKey, items]) => {
          const category = CATEGORY_NAMES[categoryKey] || {
            icon: Briefcase,
            color: 'text-muted-foreground',
            en: categoryKey,
            zhHant: categoryKey,
            zhHans: categoryKey,
          };
          const CategoryIcon = category.icon;
          const isExpanded = expandedCategory === categoryKey;
          const doneCount = items.filter((item: ComplianceItem) => item.member_status === 'compliant').length;
          return (
            <div key={categoryKey} className="overflow-hidden rounded-xl border bg-card">
              <button
                onClick={() => setExpandedCategory(isExpanded ? null : categoryKey)}
                className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <CategoryIcon className={`h-5 w-5 ${category.color}`} />
                  <div className="text-left">
                    <h3 className="font-semibold text-foreground">{categoryLabel(categoryKey)}</h3>
                    <p className="text-xs text-muted-foreground">
                      {doneCount}/{items.length} {tr('completed', '已完成', '已完成')}
                    </p>
                  </div>
                </div>
                {isExpanded ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
              </button>

              {isExpanded && (
                <div className="border-t divide-y">
                  {items.map((item) => (
                    <ComplianceRow
                      key={item.id}
                      item={item}
                      onStatusChange={(status) => updateStatus.mutate({ id: item.id, status })}
                      loading={updateStatus.isPending}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* BR / CI Info */}
      {company_info && (
        <div className="rounded-xl border bg-muted/30 p-4 text-sm">
          <h4 className="mb-2 font-semibold text-foreground">{tr('Company information', '公司基本資料', '公司基本资料')}</h4>
          <div className="grid grid-cols-2 gap-2 text-muted-foreground">
            {company_info.br_number && (
              <div>{tr('BR No.', '商業登記證號碼', '商业登记证号码')}: <span className="font-medium text-foreground">{company_info.br_number}</span></div>
            )}
            {company_info.br_expiry_date && (
              <div>{tr('BR expiry', '商業登記證到期', '商业登记证到期')}: <span className="font-medium text-foreground">{company_info.br_expiry_date}</span></div>
            )}
            {company_info.ci_number && (
              <div>{tr('CI No.', '公司註冊證書號碼', '公司注册证书号码')}: <span className="font-medium text-foreground">{company_info.ci_number}</span></div>
            )}
            {company_info.secretary_name && (
              <div>{tr('Company secretary', '公司秘書', '公司秘书')}: <span className="font-medium text-foreground">{company_info.secretary_name}</span></div>
            )}
            {company_info.auditor_name && (
              <div>{tr('Auditor', '審計師', '审计师')}: <span className="font-medium text-foreground">{company_info.auditor_name}</span></div>
            )}
            <div>{tr('Financial year end', '年結日', '年结日')}: <span className="font-medium text-foreground">{company_info.fiscal_year_end || '03-31'}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Stat Card ──
function StatCard({ label, value, color, bg, icon: Icon }: { label: string; value: number; color: string; bg: string; icon: React.ElementType }) {
  return (
    <div className={`${bg} rounded-xl p-3 text-center`}>
      <Icon className={`mx-auto mb-1 h-5 w-5 ${color}`} />
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

// ── Compliance Row ──
function ComplianceRow({ item, onStatusChange, loading }: { item: ComplianceItem; onStatusChange: (status: string) => void; loading: boolean }) {
  const { i18n } = useTranslation();
  const BadgeIcon = complianceStatus(item.member_status).icon;
  const [expanded, setExpanded] = useState(false);
  const itemTitle = tr(item.title_en || item.title_zh, item.title_zh, item.title_zh);
  const itemDescription = tr(item.title_en || item.description_zh || '', item.description_zh || '', item.description_zh || '');
  const isEnglish = i18n.language === 'en';
  const actionLabel = isEnglish
    ? tr('Open reference', '開啟參考資料', '打开参考资料')
    : (item.action_label_zh || tr('Open reference', '開啟參考資料', '打开参考资料'));

  return (
    <div id={`compliance-item-${item.id}`} className="p-3 transition-colors hover:bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <button
            onClick={() => onStatusChange(item.member_status === 'compliant' ? 'pending' : 'compliant')}
            disabled={loading}
            className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
              item.member_status === 'compliant'
                ? 'border-green-800 bg-green-800 text-white'
                : 'border-gray-300 hover:border-green-700'
            }`}
            aria-label={complianceStatusLabel(item.member_status === 'compliant' ? 'pending' : 'compliant')}
          >
            {item.member_status === 'compliant' && <CheckCircle2 className="h-3.5 w-3.5" />}
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${item.member_status === 'compliant' ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                {itemTitle}
              </span>
              {item.is_required && (
                <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-900">
                  {tr('Required', '必須', '必须')}
                </span>
              )}
            </div>
            {itemDescription && (
              <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{itemDescription}</p>
            )}
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${statusBadgeClass(item.member_status)}`}>
            <BadgeIcon className="h-3 w-3" />
            {complianceStatusLabel(item.member_status)}
          </span>
          {item.action_url && (
            <a
              href={item.action_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground transition-colors hover:text-primary"
              aria-label={actionLabel}
              title={actionLabel}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          {(item.description_zh || item.notes) && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-muted-foreground transition-colors hover:text-foreground"
              aria-expanded={expanded}
              aria-label={expanded ? tr('Hide details', '隱藏詳情', '隐藏详情') : tr('View details', '查看詳情', '查看详情')}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="ml-8 mt-2 space-y-1 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
          {item.description_zh && isEnglish && item.title_en && (
            <p>{tr('Original description', '原始描述', '原始描述')}: {item.description_zh}</p>
          )}
          {item.description_zh && !isEnglish && <p className="text-foreground">{item.description_zh}</p>}
          {item.notes && (
            <p className="text-foreground">{tr('Notes', '備註', '备注')}: {item.notes}</p>
          )}
          {item.action_url && (
            <a
              href={item.action_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              {actionLabel} <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <div className="flex gap-2 pt-1">
            {(['compliant', 'pending', 'not_applicable'] as const).map((status) => (
              <button
                key={status}
                onClick={() => onStatusChange(status)}
                disabled={loading || item.member_status === status}
                className={`rounded border px-2 py-1 text-xs transition-colors ${
                  item.member_status === status
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
              >
                {complianceStatusLabel(status)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
